/**
 * @lykbl/dev-tools - Types and interfaces for dev mode
 */

import type { User, Session } from '@supabase/supabase-js';

/**
 * App types for dev mode - determines which mock users and behaviors are available
 */
export type DevModeAppType = 'ai-amp' | 'marketing';

/**
 * Base mock user interface
 */
export interface DevModeUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  walletBalance: number;
  avatarUrl: string;
  description?: string;
}

/**
 * AI-AMP specific roles for authenticated app testing
 */
export type AiAmpDevRole = 'creator' | 'developer' | 'brand' | 'admin';

/**
 * Marketing specific roles for public site testing
 */
export type MarketingDevRole = 'visitor' | 'creator-prospect' | 'brand-prospect' | 'creator' | 'brand' | 'admin';

/**
 * Combined dev mode role type
 */
export type DevModeRole = AiAmpDevRole | MarketingDevRole;

/**
 * Context value interface
 */
export interface DevModeContextValue {
  /** Whether dev mode is currently active */
  isDevMode: boolean;
  /** Current mock user data */
  devUser: DevModeUser | null;
  /** Current selected role */
  devRole: DevModeRole | null;
  /** Supabase-compatible mock User object */
  mockUser: User | null;
  /** Supabase-compatible mock Session object */
  mockSession: Session | null;
  /** Set the current dev role */
  setDevRole: (role: DevModeRole | null) => void;
  /** Exit dev mode and clear session */
  exitDevMode: () => void;
  /** Whether dev mode is allowed in current environment */
  isAllowed: boolean;
  /** Current app type */
  appType: DevModeAppType;
}

/**
 * Provider props
 */
export interface DevModeProviderProps {
  children: React.ReactNode;
  /** Which app this is for - determines available roles */
  appType: DevModeAppType;
}

/**
 * Role configuration for UI display
 */
export interface RoleConfig {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: string;
  bgColor: string;
  description: string;
}

/**
 * Security configuration - domains where dev mode is blocked
 */
export interface SecurityConfig {
  /** Production domains - NEVER allow dev mode */
  productionDomains: string[];
  /** Preview/beta domains - NEVER allow dev mode */
  previewDomains: string[];
  /** Staging domains - Allow dev mode with authentication */
  stagingDomains: string[];
}

/**
 * Default security configuration
 * Updated for domain-split architecture
 * 
 * Dev Mode Access:
 * - Production domains: BLOCKED
 * - Preview domains: BLOCKED  
 * - Cloudflare Pages previews (*.pages.dev): BLOCKED (handled in isDevModeAllowed)
 * - Staging domains: ALLOWED (with auth gate)
 * - Localhost: ALLOWED
 */
export const DEFAULT_SECURITY_CONFIG: SecurityConfig = {
  productionDomains: [
    // Marketing production
    'lykbl.com',
    'www.lykbl.com',
    // AI-AMP production  
    'lykbl.ai',
    'www.lykbl.ai',
    'app.lykbl.ai',
    // Cloudflare Pages production (root domains)
    'lykbl.pages.dev',
    'lykbl-marketing.pages.dev',
    'lykbl-ai-amp.pages.dev',
  ],
  previewDomains: [
    // Named preview/beta subdomains
    'beta.lykbl.com',
    'preview.lykbl.com',
    'beta.lykbl.ai',
    'preview.lykbl.ai',
    // Note: Cloudflare Pages preview deployments (*.pages.dev) are blocked
    // dynamically in isDevModeAllowed() via hostname.endsWith('.pages.dev')
  ],
  stagingDomains: [
    // Only these domains get dev mode with auth gate
    'staging.lykbl.com',
    'staging.lykbl.ai',
    'staging.lykbl-marketing.pages.dev',
    'staging.lykbl-ai-amp.pages.dev',
  ],
};

